import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, List, ListItem, ListItemButton, ListItemText, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import { EventSnippet } from '../../../interfaces/IEvent'
import { usePolling } from '../../../hooks/usePolling'
import { bucketColor } from './BucketColors'
import InfoTooltip from './InfoTooltip'

interface UpcomingPlannedCardProps {
  variant?: 'compact' | 'wall'
  info?: string
}

function UpcomingPlannedCard({ variant = 'compact', info }: UpcomingPlannedCardProps) {
  const navigate = useNavigate()
  const isWall = variant === 'wall'
  const { data } = usePolling<EventSnippet[]>('/api/events/planned', 5 * 60_000)

  const upcoming = useMemo(() => {
    if (!data) return []
    const now = new Date()
    return data
      .filter((e) => e.fromDate && new Date(e.fromDate) >= now)
      .sort((a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime())
  }, [data])

  let body
  if (!data) {
    body = (
      <List dense disablePadding>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} sx={{ borderLeft: '4px solid', borderColor: 'divider', mb: 0, py: 0.5 }}>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
          </ListItem>
        ))}
      </List>
    )
  } else if (upcoming.length === 0) {
    body = <Typography variant="body2" color="text.secondary">Ingen kommende planlagt arbeid</Typography>
  } else {
    body = (
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <List dense disablePadding>
          {upcoming.map((e) => {
            const border = bucketColor(e.criticality)
            return (
              <ListItem key={e.id} disablePadding sx={{ borderLeft: `4px solid ${border}`, mb: 0 }}>
                <ListItemButton onClick={() => navigate(`/events/${e.id}`)} sx={{ py: 0.25 }}>
                  <ListItemText
                    sx={{ my: 0 }}
                    slotProps={{ secondary: { variant: 'caption' } }}
                    primary={
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {e.id}
                        </Typography>
                        <Typography variant="body2" component="span" noWrap>
                          {e.title}
                        </Typography>
                      </Stack>
                    }
                    secondary={`${format(new Date(e.fromDate), 'dd.MM HH:mm', { locale: nb })} · ${e.criticality}`}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    )
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: isWall ? { xs: 2, md: 3 } : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isWall ? '45vh' : 480,
        minHeight: 0,
      }}
    >
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1, flexShrink: 0 }}>
        <Typography
          variant={isWall ? undefined : 'h6'}
          sx={{ fontSize: isWall ? 'clamp(1.25rem, 2vw, 2.25rem)' : undefined, fontWeight: 600 }}
        >
          Kommende planlagt arbeid
        </Typography>
        {info && !isWall && <InfoTooltip text={info} />}
      </Stack>
      {body}
    </Paper>
  )
}

export default UpcomingPlannedCard
